import { useMemo } from 'react';

import { Box, List } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Switch } from '@repo/videa-components/src/ui/switch';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

import { VideaInfoElement } from '../../../../common-components/composed/videaInfoElement/VideaInfoElement';
import { blueBlack700 } from '../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
// eslint-disable-next-line import/no-deprecated -- Deprecated: Use the new useFeatureFlag hook
import { ModelHashes } from '../../types';
import { useGetModelFeatureFlags } from '../../utils';
import { useFindingSidebarTranslations } from '../constants';

import { displayFindingList } from './utils';

const useStyles = makeStyles(() => ({
  findingList: {
    margin: 0,
    padding: 0,
    color: 'white',
  },
  listHeader: {
    display: 'flex',
    padding: '1em 1em .5em',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: blueBlack700,
    color: 'white',
    zIndex: 0,
    fontFamily: 'proxima-nova, sans-serif',
    fontWeight: '100',
    fontStyle: 'normal',
    fontSize: '20px',
    lineHeight: '33px',
    textTransform: 'none',
    position: 'static',
    flexWrap: 'nowrap',
    gap: '.125em',
  },
  spacer: {
    flex: '0 0 100%',
  },
  tooltip: {
    width: 225,
    maxWidth: 225,
  },
  infoIcon: {
    fontSize: '12px',
    verticalAlign: 'text-bottom',
    marginLeft: '3px',
  },
}));

const FindingGroupHeader = (props: any) => {
  const {
    children,
    findingGroup = {},
    enabledFindingTypes = {},
    canEnable = true,
    onSetEnabledFindingTypes = () => {},
    sortedAndFilteredImages = [],
  } = props;

  const { id: selectedPatientId } = useParams<{ id?: string }>();
  const { trackEvent } = useTrackEvent();
  const { fdaStatusInfo, typeToHeaderMap } = useFindingSidebarTranslations();

  const classes = useStyles();

  const { isCariesSubclassEnabled, isVdaEnabled } = useGetModelFeatureFlags();

  // Move the model hashes calculation to a useMemo hook to update when images change
  const { isVdaModel } = useMemo(() => {
    const modelHashesList = sortedAndFilteredImages
      .filter((img: any) => img.modelHashes)
      .map((img: any) => img.modelHashes);

    const keys =
      modelHashesList.length > 0 && modelHashesList[0]
        ? Object.keys(modelHashesList[0])
        : [];

    return {
      modelHashesKeys: keys,
      isVdaModel: keys.find(
        (key) => key === ModelHashes.CARIES_BW || key === ModelHashes.CARIES_PA,
      ),
    };
  }, [sortedAndFilteredImages]);

  const toggleFindingGroup = () => {
    let newFindingsTypes = {};
    if (findingGroup.type == 'caries') {
      newFindingsTypes = {
        ...enabledFindingTypes,
        caries: !enabledFindingTypes.caries,
        incipients: !enabledFindingTypes.caries,
      };
    } else {
      newFindingsTypes = {
        ...enabledFindingTypes,
        [findingGroup.type]: !enabledFindingTypes[findingGroup.type],
      };
    }
    trackEvent(mixpanelEvents.toggleFindings, {
      toggled: {
        category: findingGroup.type,
        // if the new list includes the toggled value, then the toggled category was toggled on
        value: !enabledFindingTypes[findingGroup.type],
      },
      previousFindingsTypes: enabledFindingTypes,
      newFindingsTypes: newFindingsTypes,
      patientId: selectedPatientId,
    });

    onSetEnabledFindingTypes(newFindingsTypes);
  };

  const isFindingTypeVisible = enabledFindingTypes[findingGroup.type];

  const displayFdaStatusMessage = (groupType: keyof typeof fdaStatusInfo) => {
    const messageInfoByGroup =
      groupType === 'caries'
        ? isVdaModel
          ? fdaStatusInfo.caries
          : fdaStatusInfo.VDA
        : fdaStatusInfo[groupType];

    return <VideaInfoElement title={messageInfoByGroup?.title || ''} />;
  };

  return (
    <div className="sentry-unmask">
      <List
        aria-labelledby="nested-list-subheader"
        className={classes.findingList}
        subheader={
          displayFindingList(
            findingGroup.type,
            isCariesSubclassEnabled,
            isVdaEnabled,
          ) && (
            <div id="nested-list-subheader" className={classes.listHeader}>
              {/** @ts-expect-error TS(2769): No overload matches this call. */}
              <Box display="flex-wrap" direction="row">
                {/** @ts-expect-error TS(2769): No overload matches this call. */}
                <Box
                  display="flex"
                  direction="column"
                  alignItems="center"
                  justify-content="space-between"
                >
                  <p className="vai-text-white vai-text-[16px] vai-font-normal vai-leading-[24px]">
                    {/** @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
                    {typeToHeaderMap[findingGroup.type] || 'Show Findings'}
                  </p>
                </Box>
                {displayFdaStatusMessage(findingGroup.type)}
              </Box>
              <Switch
                data-cy={`toggle-${findingGroup.type}`}
                dataTestId={`toggle-${findingGroup.type}`}
                disabled={!canEnable}
                checked={isFindingTypeVisible}
                onCheckedChange={toggleFindingGroup}
              />
            </div>
          )
        }
      >
        {children}
      </List>
    </div>
  );
};

FindingGroupHeader.propTypes = {
  canEnable: PropTypes.bool,
  children: PropTypes.element,
  enabledFindingTypes: PropTypes.object,
  findingGroup: PropTypes.object,
  sortedAndFilteredImages: PropTypes.array,
  user: PropTypes.object,
  onSetEnabledFindingTypes: PropTypes.func,
};

export default FindingGroupHeader;
